// One visual structure for the error/status family (404, /error, unsupported
// app, maintenance, realtime-DB failures): icon in a soft tinted circle →
// title → copy → actions. Replaces v3's antd Result illustrations with the
// take-hero circle idiom. Dependency-light on purpose (React + cn only) — the
// unsupported-app page renders inside lockdown clients before the app boots.
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type APStatusTone = 'danger' | 'warning' | 'primary' | 'neutral';

const TONE_CLASSES: Record<APStatusTone, string> = {
  danger: 'bg-ap-destructive-a10 text-destructive',
  warning: 'bg-ap-warning-a10 text-ap-warning',
  primary: 'bg-ap-primary-a10 text-primary',
  neutral: 'bg-muted text-muted-foreground',
};

export interface APStatusViewProps {
  icon: ReactNode;
  tone?: APStatusTone;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Standalone route surfaces: full viewport on the page canvas. */
  fullScreen?: boolean;
  className?: string;
  testId: string;
}

export const APStatusView = ({
  icon,
  tone = 'neutral',
  title,
  description,
  actions,
  fullScreen = false,
  className,
  testId,
}: APStatusViewProps) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-4 p-8 text-center text-foreground',
      fullScreen && 'min-h-screen bg-ap-canvas',
      className
    )}
    data-testid={testId}
  >
    <div
      className={cn(
        'flex size-20 items-center justify-center rounded-full [&_svg]:size-10',
        TONE_CLASSES[tone]
      )}
      data-testid={`${testId}-icon`}
      aria-hidden="true"
    >
      {icon}
    </div>
    <p className="text-2xl font-semibold">{title}</p>
    {description && (
      <div className="max-w-prose whitespace-pre-line text-sm text-muted-foreground">
        {description}
      </div>
    )}
    {actions && <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div>}
  </div>
);
